import { useAbility } from '@/modules/identity/application/ability';
import type { AutoBlockConfig, UnitCobranza } from '@yawi/types';
import { AlertTriangle, Settings } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { AccessConfirmModal, type AccessConfirmState } from '../components/AccessConfirmModal';
import { AutoBlockModal } from '../components/AutoBlockModal';
import { BulkActionBar } from '../components/BulkActionBar';
import { CobranzaSummaryCards } from '../components/CobranzaSummaryCards';
import { UnidadesTable } from '../components/UnidadesTable';
import { useAutoBlockConfig } from '../hooks/useAutoBlockConfig';
import { useBulkAccess } from '../hooks/useBulkAccess';
import { useCobranza } from '../hooks/useCobranza';
import { useUnitAccess } from '../hooks/useUnitAccess';

const configBadge = (config: AutoBlockConfig | null): string => {
  if (!config || !config.enabled) return 'Inactivo';
  const label = config.monthsThreshold === 1 ? 'mes' : 'meses';
  return `Deber ${config.monthsThreshold} ${label} · día ${config.dayOfMonth}`;
};

export const CobranzaPage: React.FC = () => {
  const ability = useAbility();

  const {
    units,
    summary,
    loading,
    error,
    filter,
    setFilter,
    sort,
    setSort,
    query,
    setQuery,
    reload,
    refetchSummary,
    patchUnit,
    patchMany,
  } = useCobranza();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirm, setConfirm] = useState<AccessConfirmState | null>(null);

  const clearSelection = () => setSelectedIds([]);

  // Tras un cambio exitoso recargamos la lista y el resumen para resincronizar
  // (orden, filtro activo, conteos).
  const onAccessChanged = () => {
    void reload();
    void refetchSummary();
  };

  const { toggle, pendingId } = useUnitAccess({
    patchUnit,
    onChanged: onAccessChanged,
  });

  const { run: runBulk, loading: bulkLoading } = useBulkAccess({
    patchMany,
    onChanged: onAccessChanged,
    clearSelection,
  });

  // El bloqueo automático aplica a todo el residencial: solo el admin global lo configura.
  const canConfigAutoBlock = ability.can('manage', 'all');
  const { config, loading: configLoading, saving, save } = useAutoBlockConfig(canConfigAutoBlock);

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const selectWithDebt = () =>
    setSelectedIds(units.filter((u) => u.debtAmount > 0).map((u) => u.id));

  const badge = useMemo(() => configBadge(config), [config]);

  // El toggle individual y las acciones bulk piden confirmación antes de aplicar.
  const requestToggle = (unit: UnitCobranza) =>
    setConfirm({ units: [unit], block: !unit.isBlocked });

  const requestBulk = (block: boolean) => {
    const list = units.filter((u) => selectedIds.includes(u.id));
    if (list.length > 0) setConfirm({ units: list, block });
  };

  const confirmAccess = async () => {
    if (!confirm) return;
    if (confirm.units.length === 1) {
      await toggle(confirm.units[0]);
    } else {
      await runBulk(
        confirm.units.map((u) => u.id),
        confirm.block,
      );
    }
    setConfirm(null);
  };

  const confirmLoading = pendingId !== null || bulkLoading;

  if (!ability.can('read', 'Cobranza') && !ability.can('manage', 'Cobranza')) {
    return (
      <div className="flex items-center justify-center p-12 text-slate-400">
        No tienes permisos para ver esta sección.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 pb-24">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Cobranza</h1>
          <p className="mt-1 text-sm text-slate-500">
            Gestiona el acceso de unidades morosas de forma individual o masiva.
          </p>
        </div>
        {canConfigAutoBlock && (
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="inline-flex cursor-pointer items-center gap-3 self-start rounded-2xl border border-violet-200 bg-white px-3 py-2.5 hover:bg-violet-50/60"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-100 text-[#7C5CFC]">
              <Settings size={20} />
            </span>
            <span className="pr-2 text-left">
              <span className="block text-xs font-medium text-slate-400">Bloqueo automático</span>
              <span className="block text-sm font-bold text-slate-800">{badge}</span>
            </span>
          </button>
        )}
      </div>

      {error ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-red-100 bg-red-50/50 p-12 text-center">
          <AlertTriangle size={32} className="text-red-400" />
          <p className="text-sm text-slate-600">{error}</p>
          <button
            type="button"
            onClick={() => void reload()}
            className="rounded-lg bg-[#7C5CFC] px-4 py-2 text-sm font-medium text-white hover:bg-[#6b4ce0]"
          >
            Reintentar
          </button>
        </div>
      ) : (
        <>
          <CobranzaSummaryCards
            summary={summary}
            loading={loading && !summary}
            activeFilter={filter}
            onFilterChange={setFilter}
          />

          <UnidadesTable
            units={units}
            loading={loading}
            filter={filter}
            query={query}
            onQueryChange={setQuery}
            sort={sort}
            onSortChange={setSort}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onSelectVisible={setSelectedIds}
            onSelectWithDebt={selectWithDebt}
            onClearSelection={clearSelection}
            pendingAccessId={pendingId}
            onToggleAccess={requestToggle}
          />
        </>
      )}

      <BulkActionBar
        count={selectedIds.length}
        loading={bulkLoading}
        onBlock={() => requestBulk(true)}
        onEnable={() => requestBulk(false)}
        onClear={clearSelection}
      />

      <AccessConfirmModal
        state={confirm}
        loading={confirmLoading}
        onConfirm={() => void confirmAccess()}
        onClose={() => setConfirm(null)}
      />

      <AutoBlockModal
        open={modalOpen}
        loading={configLoading}
        saving={saving}
        config={config}
        onClose={() => setModalOpen(false)}
        onSave={save}
      />
    </div>
  );
};
